import React, { useEffect, useState } from "react";
import { Paper, Box, TextField, Typography, Alert, IconButton, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { pdf as PDF } from "@react-pdf/renderer";
import { ApiService } from "../../ApiService";
import buscar_azul from "../../../images/search_azul.png";
import ErrorIcon from "@mui/icons-material/Error";
import Snackbar from "@mui/material/Snackbar";
import { useParams } from "react-router-dom";
import { GeneratePdfReporteVentas } from "../../../Reports/GeneratePdfReporteVentas";
import Button from "@mui/material/Button";
import pdf from "../../../images/pdf.png";
import { useCompanyAssigned } from "../../CompanyAssignedContext";
import LupaIcon from "../../../svg/lupaIcon";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { toast } from "react-toastify";
import SearchCompany from "../../../pages/empresa_uno/SearchCompany";

export const VreporteVentas = ({ selectedSucursal, permisos, convertPrice = (() => { }) }) => {
	const { nit, sub_direc } = useParams();
	const [statePermission, setStatePermission] = useState(false);
	const [sucursales, setSucursales] = useState([]);
	const [objSales, setObjSales] = useState([]);
	const [fechaIni, setFechaIni] = useState(null);
	const [fechaFin, setFechaFin] = useState(null);
	const [isPdfLoading, setPdfLoading] = useState(false);

	// Cargar las ventas
	const getCurrentVentas = async (key_branch, fecha1, fecha2) => {
		try {
			const url = `api/company/sale/reports?start_date=${fecha1}&end_date=${fecha2}&key_branch=${key_branch}`;
			const options = { method: "GET" };

			const request_totals = await ApiService.fetchData(url, options);

			if (!request_totals) {
				toast.error("Error al cargar los datos de ventas");
				return;
			}

			// Asignar IDs únicos para DataGrid
			const salesWithIds = request_totals.map((sale) => ({
				...sale,
				id: `${sale.nro}_${sale.authCode}_${sale.code}`,
				totalPrice: (parseFloat(sale.price) * parseInt(sale.qp)).toFixed(2),
			}));

			setObjSales(salesWithIds);

			if (request_totals && request_totals.length > 0 && request_totals[0].cost !== undefined) {
				setStatePermission(true);
			}
		} catch (error) {
			toast.error("Error al cargar los datos de ventas");
		}
	};

	const handleViewOrder = async () => {
		try {
			setPdfLoading(true);
			const url = `api/company/sale/pdf-reports?key_branch=${selectedSucursal}&start_date=${fechaIni}&end_date=${fechaFin}`;
			const options = { method: "GET" };
			const access = !permisos?.remove_costo && !permisos?.remove_bene ? true : false;
			const saleOrderData = await ApiService.fetchData(url, options);
			if (saleOrderData !== null && saleOrderData.details.length > 0) {
				const MyDoc = <GeneratePdfReporteVentas pdfData={saleOrderData} canViewCostoBeneficio={access} themeColor={'var(--theme-company)'} convertPrice={convertPrice} />;
				const asPdf = PDF(MyDoc);
				const blob = await asPdf.toBlob();
				const fileURL = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = fileURL;
				link.download = "ReporteVenta.pdf";
				link.click();
				URL.revokeObjectURL(fileURL);
				setPdfLoading(false);
			} else {
				toast.info("No se encontró la información de la orden de venta");
				setPdfLoading(false);
			}
		} catch (error) {
			toast.error("Error al generar la orden de venta");
			setPdfLoading(false);
		}
	};

	useEffect(() => {
		const current_suc = selectedSucursal;
		const comp_date = new Date();
		const currentDate = `${comp_date.getFullYear()}-${String(comp_date.getMonth() + 1).padStart(2, "0")}-${String(comp_date.getDate()).padStart(2, "0")}`;

		if (fechaIni == null && fechaFin == null) {
			setFechaFin(currentDate);
			setFechaIni(currentDate);
		}

		setSucursales(selectedSucursal);
		getCurrentVentas(selectedSucursal, fechaIni || currentDate, fechaFin || currentDate);
	}, [selectedSucursal]);

	function handleChange(e, comp) {
		if (comp === 1) {
			setFechaIni(e);
			getCurrentVentas(sucursales, e, fechaFin);
		} else {
			setFechaFin(e);
			getCurrentVentas(sucursales, fechaIni, e);
		}
	}

	// Totales
	const totales = {
		cantidad: objSales?.reduce((acc, venta) => acc + venta.qp, 0) || 0,
		costo:
			objSales
				?.reduce((acc, venta) => {
					return parseFloat(statePermission ? venta.cost : 0) * parseInt(venta.qp) + parseFloat(acc);
				}, 0)
				.toFixed(2) || 0,
		beneficio:
			objSales
				?.reduce((acc, venta) => {
					return parseFloat(statePermission ? venta.earning : 0) * parseInt(venta.qp) + parseFloat(acc);
				}, 0)
				.toFixed(2) || 0,
		precio:
			objSales?.reduce((acc, venta) => {
				return parseFloat(parseFloat(acc) + parseFloat(venta.price) * parseInt(venta.qp)).toFixed(2);
			}, 0) || 0,
	};
	const baseColumns = [
		{ field: "nro", headerName: "N°", flex: 0.7 },
		{ field: "date_sale", headerName: "Fecha", flex: 1 },
		{ field: "client_name", headerName: "Cliente", flex: 2 },
		{ field: "code", headerName: "Código", flex: 1 },
		{ field: "product_name", headerName: "Producto", flex: 2 },
		{
			field: "qp",
			headerName: "Cantidad",
			flex: 0.8,
			align: "center",
			headerAlign: "center",
		},
	];
	const costBenefitColumns = !permisos?.remove_costo && !permisos?.remove_bene
		? [
			{
				field: "cost_total",
				headerName: "Costo",
				flex: 1,
				renderCell: (params) => <Box>{convertPrice(params.row.cost * params.row.qp, null, true)}</Box>,
			},
			{
				field: "earning_total",
				headerName: "Beneficio",
				flex: 1,
				renderCell: (params) => <Box>{convertPrice(params.row.earning * params.row.qp, null, true)}</Box>,
			},
		]
		: [];
	const priceAndActionColumns = [
		{
			field: "totalPrice",
			headerName: "Precio",
			flex: 1,
			align: "center",
			headerAlign: "center",
			renderCell: (params) => (
				<Box sx={{ display: "flex", justifyContent: "center" }}>
					{convertPrice((parseFloat(params.row.price) * parseInt(params.row.qp)), null, true)}
				</Box>
			),
		},
	];
	const columns = [...baseColumns, ...costBenefitColumns, ...priceAndActionColumns];

	// Custom footer component
	const CustomFooter = () => {
		return (
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					bgcolor: 'var(--theme-company)',
					color: "white",
					p: 1,
					mt: 1,
				}}>
				{/* N° column - Total label */}
				<Box sx={{ flex: 0.7 }}>
					<Typography variant="caption" fontWeight="bold">
						Total
					</Typography>
				</Box>

				{/* Empty cells for date, cliente, código, producto */}
				<Box sx={{ flex: 1 }}></Box>
				<Box sx={{ flex: 2 }}></Box>
				<Box sx={{ flex: 1 }}></Box>
				<Box sx={{ flex: 2 }}></Box>

				{/* Cantidad column - total quantity */}
				<Box sx={{ flex: 0.8, display: "flex", justifyContent: "center" }}>
					<Typography variant="caption" fontWeight="bold">
						{totales.cantidad}
					</Typography>
				</Box>

				{/* Conditional Cost/Beneficio columns */}
				{!permisos?.remove_costo && !permisos?.remove_bene && (
					<>
						<Box sx={{ flex: 1, display: "flex", justifyContent: "flex-start", pl: 1 }}>
							<Typography variant="caption" fontWeight="bold">
								{convertPrice(totales.costo)}
							</Typography>
						</Box>
						<Box sx={{ flex: 1, display: "flex", justifyContent: "flex-start", pl: 1 }}>
							<Typography variant="caption" fontWeight="bold">
								{convertPrice(totales.beneficio)}
							</Typography>
						</Box>
					</>
				)}

				{/* Precio column - total price */}
				<Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
					<Typography variant="caption" fontWeight="bold">
						{convertPrice(totales.precio)}
					</Typography>
				</Box>

				{/* Actions column - empty */}
				<Box sx={{ flex: 0.5 }}></Box>
			</Box>
		);
	};

	// No data overlay
	const NoDataOverlay = () => {
		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					height: "100%",
				}}>
				<ErrorIcon />
				<Typography variant="h6" color="textSecondary">
					No hay ventas en el período seleccionado.
				</Typography>
			</Box>
		);
	};

	return (
		<div>
			<div className="functionality-container">
				<div className="header-reports">
					<h2 style={{ color: 'var(--theme-company)' }}>Reporte de Ventas</h2>

					<div style={{ display: "flex", flexDirection: "row", gap: "20px" }}>
						<TextField
							label="Fecha Inicial"
							type="date"
							value={fechaIni}
							onChange={(e) => handleChange(e.target.value, 1)}
							size="small"
							sx={{ width: "150px" }}
							InputLabelProps={{
								shrink: true,
							}}
						/>

						<TextField
							label="Fecha Fin"
							type="date"
							value={fechaFin}
							onChange={(e) => handleChange(e.target.value, 2)}
							size="small"
							sx={{ width: "150px" }}
							InputLabelProps={{
								shrink: true,
							}}
						/>
					</div>

					<div>
						<SearchCompany theme={'var(--theme-company)'} placeholder="reporte de venta" />

						<div>
							{permisos?.pdf && (
								<Button variant="contained" sx={{ height: 41, background: 'var(--theme-company)' }} loading={isPdfLoading} size="small" onClick={handleViewOrder} startIcon={<PictureAsPdfIcon />} >
									<span style={{ color: "#fff" }}>PDF</span>
								</Button>
							)}
						</div>
					</div>
				</div>
			</div>

			<Paper sx={{ width: "100%", height: 500 }}>
				<DataGrid
					rows={objSales}
					columns={columns}
					autosizeOnMount={true}
					disableColumnMenu={true}
					pageSizeOptions={[5, 10, 25, 50]}
					pagination
					disableRowSelectionOnClick
					localeText={{
						MuiTablePagination: {
							labelRowsPerPage: "Filas por pagina:",
							labelDisplayedRows: ({ from, to, count }) =>
								`${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`,
						},
					}}
					initialState={{
						sorting: {
							sortModel: [{ field: "nro", sort: "asc" }],
						},
						pagination: {
							paginationModel: { pageSize: 10, page: 0 },
						},
					}}
					slots={{
						footer: CustomFooter,
						noRowsOverlay: NoDataOverlay,
					}}
					sx={{
						"& .MuiDataGrid-columnHeader": {
							backgroundColor: 'var(--theme-company)',
							color: "white",
							fontWeight: "bold",
						},
					}}
				/>
			</Paper>
		</div>
	);
};
